/**
 * Wave 29 Chunk 7 — Employee pay-rate + working-hours panel.
 *
 * Iter 357 — 4-way OT multipliers (Weekday / Sat / Sun / Public holiday).
 * Iter 362 (2026-02-27) — Dirk: "there's no place in profile to
 *   actually set the normal working hours … the result of that is
 *   that every single hour worked shows as overtime because there's
 *   no section for standard time."
 *   Added per-weekday schedule (enabled + start + end + lunch) so the
 *   attendance engine can split ordinary vs OT correctly.
 *
 * HR-only block on /team/{uid} that captures everything the payroll +
 * attendance register needs about an employee's paid time.
 */
import { useEffect, useState } from "react";
import { Wallet, Save, Loader2, Clock } from "lucide-react";
import api from "../../lib/api";
import { toast } from "../ui/sonner";

const PERIODS = [
  { v: "hourly", l: "per hour" },
  { v: "daily", l: "per day" },
  { v: "weekly", l: "per week" },
  { v: "monthly", l: "per month" },
];

const WEEKDAYS = [
  { k: "mon", label: "Monday" },
  { k: "tue", label: "Tuesday" },
  { k: "wed", label: "Wednesday" },
  { k: "thu", label: "Thursday" },
  { k: "fri", label: "Friday" },
  { k: "sat", label: "Saturday" },
  { k: "sun", label: "Sunday" },
];

const DEFAULT_SCHEDULE = {
  mon: { enabled: true,  start: "07:00", end: "16:30", lunch_min: 30 },
  tue: { enabled: true,  start: "07:00", end: "16:30", lunch_min: 30 },
  wed: { enabled: true,  start: "07:00", end: "16:30", lunch_min: 30 },
  thu: { enabled: true,  start: "07:00", end: "16:30", lunch_min: 30 },
  fri: { enabled: true,  start: "07:00", end: "16:30", lunch_min: 30 },
  sat: { enabled: false, start: "07:00", end: "12:00", lunch_min: 0 },
  sun: { enabled: false, start: "07:00", end: "12:00", lunch_min: 0 },
};

function slotOrdinaryMin(slot) {
  if (!slot || !slot.enabled) return 0;
  try {
    const [sh, sm] = String(slot.start || "07:00").split(":").map(Number);
    const [eh, em] = String(slot.end || "16:30").split(":").map(Number);
    const span = (eh * 60 + em) - (sh * 60 + sm) - Number(slot.lunch_min || 0);
    return Math.max(0, span);
  } catch { return 0; }
}
const fmtMin = (m) => `${(m / 60).toFixed(2)} h`;

export default function PayRateBlock({ userId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [amount, setAmount] = useState("");
  const [period, setPeriod] = useState("monthly");
  const [weekdayMul, setWeekdayMul] = useState("1.5");
  const [saturdayMul, setSaturdayMul] = useState("1.5");
  const [sundayMul, setSundayMul] = useState("2");
  const [holidayMul, setHolidayMul] = useState("2");
  const [currency, setCurrency] = useState("ZAR");
  const [leaveDays, setLeaveDays] = useState("21");
  const [schedule, setSchedule] = useState(DEFAULT_SCHEDULE);
  const [weeklyOrd, setWeeklyOrd] = useState("45");
  const [otPaid, setOtPaid] = useState(true);
  // Iter 372.p3 — Weekly balance / short-hours policy
  const [otPolicy, setOtPolicy] = useState("strict_daily");
  const [allowShortPay, setAllowShortPay] = useState(false);
  const [shortPrompt, setShortPrompt] = useState("ask");

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/team/employees/${userId}/pay`);
      setData(d || {});
      if (d) {
        setAmount(d.pay_rate_amount != null ? String(d.pay_rate_amount) : "");
        setPeriod(d.pay_rate_period || "monthly");
        const legacyOt = d.overtime_multiplier ?? 1.5;
        setWeekdayMul(String(d.weekday_ot_multiplier ?? legacyOt));
        setSaturdayMul(String(d.saturday_multiplier ?? 1.5));
        setSundayMul(String(d.sunday_multiplier ?? 2));
        setHolidayMul(String(d.public_holiday_multiplier ?? 2));
        setCurrency(d.pay_currency || "ZAR");
        setLeaveDays(String(d.annual_leave_days_per_year ?? 21));
        setWeeklyOrd(String(d.weekly_ordinary_hours ?? 45));
        setOtPaid(d.overtime_paid !== false);
        if (d.overtime_policy) setOtPolicy(d.overtime_policy);
        if (d.allow_short_pay !== undefined) setAllowShortPay(!!d.allow_short_pay);
        if (d.short_hours_prompt) setShortPrompt(d.short_hours_prompt);
        if (d.schedule && typeof d.schedule === "object") {
          setSchedule({ ...DEFAULT_SCHEDULE, ...d.schedule });
        }
      }
    } catch (_e) { /* noop */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  const setSlot = (dayKey, patch) =>
    setSchedule((prev) => ({ ...prev, [dayKey]: { ...prev[dayKey], ...patch } }));

  const save = async () => {
    setSaving(true);
    try {
      const weekday = parseFloat(weekdayMul || "1.5");
      const { data: d } = await api.patch(`/team/employees/${userId}/pay`, {
        pay_rate_amount: parseFloat(amount || "0"),
        pay_rate_period: period,
        overtime_multiplier: weekday,
        weekday_ot_multiplier: weekday,
        saturday_multiplier: parseFloat(saturdayMul || "1.5"),
        sunday_multiplier: parseFloat(sundayMul || "2"),
        public_holiday_multiplier: parseFloat(holidayMul || "2"),
        pay_currency: currency.toUpperCase(),
        annual_leave_days_per_year: parseInt(leaveDays || "21", 10),
        schedule,
        weekly_ordinary_hours: parseFloat(weeklyOrd || "45"),
        overtime_paid: !!otPaid,
        overtime_policy: otPolicy,
        allow_short_pay: !!allowShortPay,
        short_hours_prompt: shortPrompt,
      });
      setData(d);
      toast.success("Pay rate & working hours updated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="h-24 hv-card animate-pulse" />;

  const totalWeeklyOrdinaryMin = WEEKDAYS.reduce(
    (s, w) => s + slotOrdinaryMin(schedule[w.k]),
    0,
  );
  const overCap = totalWeeklyOrdinaryMin > parseFloat(weeklyOrd || "45") * 60;

  return (
    <div className="hv-card p-4 space-y-4" data-testid="pay-rate-block">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] inline-flex items-center gap-1">
        <Wallet className="w-3 h-3" /> Pay rate &amp; overtime
      </div>
      {/* Row 1: base rate */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <label className="text-[10px] text-[#647087] block">
          Amount
          <input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="hv-input text-sm w-full mt-1" data-testid="pay-amount" />
        </label>
        <label className="text-[10px] text-[#647087] block">
          Period
          <select value={period} onChange={(e) => setPeriod(e.target.value)} className="hv-input text-sm w-full mt-1" data-testid="pay-period">
            {PERIODS.map((p) => <option key={p.v} value={p.v}>{p.l}</option>)}
          </select>
        </label>
        <label className="text-[10px] text-[#647087] block">
          Currency
          <input type="text" maxLength={3} value={currency} onChange={(e) => setCurrency(e.target.value)} className="hv-input text-sm w-full mt-1 uppercase" data-testid="pay-currency" />
        </label>
        <label className="text-[10px] text-[#647087] block">
          Annual leave (d/yr)
          <input type="number" min="0" max="60" value={leaveDays} onChange={(e) => setLeaveDays(e.target.value)} className="hv-input text-sm w-full mt-1" data-testid="pay-leave-days" />
        </label>
      </div>

      {/* Row 2: OT multipliers per type */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-1">Overtime multipliers</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <label className="text-[10px] text-[#647087] block">
            Weekday OT ×
            <input type="number" min="1" max="4" step="0.05" value={weekdayMul} onChange={(e) => setWeekdayMul(e.target.value)} className="hv-input text-sm w-full mt-1" data-testid="pay-ot-weekday" />
          </label>
          <label className="text-[10px] text-[#647087] block">
            Saturday ×
            <input type="number" min="1" max="4" step="0.05" value={saturdayMul} onChange={(e) => setSaturdayMul(e.target.value)} className="hv-input text-sm w-full mt-1" data-testid="pay-ot-saturday" />
          </label>
          <label className="text-[10px] text-[#647087] block">
            Sunday ×
            <input type="number" min="1" max="4" step="0.05" value={sundayMul} onChange={(e) => setSundayMul(e.target.value)} className="hv-input text-sm w-full mt-1" data-testid="pay-ot-sunday" />
          </label>
          <label className="text-[10px] text-[#647087] block">
            Public holiday ×
            <input type="number" min="1" max="4" step="0.05" value={holidayMul} onChange={(e) => setHolidayMul(e.target.value)} className="hv-input text-sm w-full mt-1" data-testid="pay-ot-holiday" />
          </label>
        </div>
      </div>

      {/* Iter 362 — Working-hours schedule per weekday. */}
      <div className="border-t border-white/5 pt-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] inline-flex items-center gap-1">
            <Clock className="w-3 h-3" /> Working hours
          </div>
          <label className="text-[10px] text-[#647087] inline-flex items-center gap-1.5">
            <input type="checkbox" checked={otPaid} onChange={(e) => setOtPaid(e.target.checked)} data-testid="ot-paid-toggle" />
            Overtime is paid
          </label>
        </div>
        <p className="text-[10px] text-[#a0aabf] mb-2">
          Enable the days this employee works and set their start / end
          times + lunch break. Hours above the day&apos;s scheduled span flip
          to overtime; disabled days are all-OT.
        </p>

        <div className="space-y-1.5">
          {WEEKDAYS.map((w) => {
            const slot = schedule[w.k] || {};
            const mins = slotOrdinaryMin(slot);
            return (
              <div key={w.k} className="grid grid-cols-[100px_60px_1fr_1fr_1fr_70px] gap-2 items-center text-xs" data-testid={`sched-row-${w.k}`}>
                <span className="text-[#c6ccd7]">{w.label}</span>
                <label className="inline-flex items-center gap-1">
                  <input
                    type="checkbox"
                    checked={!!slot.enabled}
                    onChange={(e) => setSlot(w.k, { enabled: e.target.checked })}
                    data-testid={`sched-${w.k}-enabled`}
                  />
                  <span className="text-[10px] text-[#647087]">On</span>
                </label>
                <input
                  type="time"
                  value={slot.start || "07:00"}
                  onChange={(e) => setSlot(w.k, { start: e.target.value })}
                  className="hv-input text-xs w-full"
                  disabled={!slot.enabled}
                  data-testid={`sched-${w.k}-start`}
                />
                <input
                  type="time"
                  value={slot.end || "16:30"}
                  onChange={(e) => setSlot(w.k, { end: e.target.value })}
                  className="hv-input text-xs w-full"
                  disabled={!slot.enabled}
                  data-testid={`sched-${w.k}-end`}
                />
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    max="180"
                    step="5"
                    value={slot.lunch_min ?? 0}
                    onChange={(e) => setSlot(w.k, { lunch_min: Number(e.target.value || 0) })}
                    className="hv-input text-xs w-full pr-10"
                    disabled={!slot.enabled}
                    data-testid={`sched-${w.k}-lunch`}
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-[#647087]">min</span>
                </div>
                <span className={`text-[10px] font-mono text-right ${slot.enabled ? "text-[#1FB6C1]" : "text-[#4a5162]"}`}>
                  {slot.enabled ? fmtMin(mins) : "off"}
                </span>
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
          <div className="text-[10px] text-[#647087]">
            <span className="text-[#a0e6ea]">Weekly scheduled ordinary:</span>{" "}
            <span className={`font-mono ${overCap ? "text-rose-300" : "text-[#c6ccd7]"}`}>
              {fmtMin(totalWeeklyOrdinaryMin)}
            </span>
            {overCap && (
              <span className="text-rose-300 ml-1.5">
                — exceeds {weeklyOrd} h/week cap
              </span>
            )}
          </div>
          <label className="text-[10px] text-[#647087] inline-flex items-center gap-1.5">
            Legal weekly cap
            <input
              type="number"
              min="0"
              max="60"
              step="0.5"
              value={weeklyOrd}
              onChange={(e) => setWeeklyOrd(e.target.value)}
              className="hv-input text-xs w-16"
              data-testid="weekly-ordinary-cap"
            />
            hours
          </label>
        </div>
      </div>

      {/* Iter 372.p3 — Weekly balance / short-hours policy per user */}
      <div className="border-t border-white/5 pt-3 space-y-2">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">
          Weekly balance & short-hours policy
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <label className="text-[10px] text-[#647087] block">
            Overtime policy
            <select
              value={otPolicy}
              onChange={(e) => setOtPolicy(e.target.value)}
              className="hv-input text-xs w-full mt-1"
              data-testid="pay-ot-policy"
            >
              <option value="strict_daily">Strict daily (BCEA default)</option>
              <option value="weekly_balance">Weekly balance</option>
            </select>
            <span className="text-[9px] text-[#647087] block mt-1 leading-tight">
              Strict: &gt; scheduled/day = OT. Weekly: OT only above 45h/week; short days offset.
            </span>
          </label>
          <label className="text-[10px] text-[#647087] block">
            Short-hours prompt
            <select
              value={shortPrompt}
              onChange={(e) => setShortPrompt(e.target.value)}
              className="hv-input text-xs w-full mt-1"
              data-testid="pay-short-prompt"
            >
              <option value="ask">Ask on register</option>
              <option value="auto_balance">Auto-balance</option>
              <option value="ignore">Ignore (pay OT gross)</option>
            </select>
            <span className="text-[9px] text-[#647087] block mt-1 leading-tight">
              What to do when a week has both OT and short-hours.
            </span>
          </label>
          <label className="text-[10px] text-[#647087] inline-flex items-start gap-1.5 mt-3">
            <input
              type="checkbox"
              checked={allowShortPay}
              onChange={(e) => setAllowShortPay(e.target.checked)}
              className="mt-0.5"
              data-testid="pay-allow-short-pay"
            />
            <span>
              Allow short pay
              <span className="text-[9px] text-[#647087] block leading-tight">
                Employer may dock pay for days worked below scheduled hours.
              </span>
            </span>
          </label>
        </div>
      </div>

      <div className="flex items-center justify-between border-t border-white/5 pt-2">
        <span className="text-[10px] text-[#647087]">
          {data?.updated_at ? `Last updated ${new Date(data.updated_at).toLocaleDateString("en-ZA")}` : "Not set yet"}
        </span>
        <button onClick={save} disabled={saving} className="hv-btn hv-btn-primary text-xs disabled:opacity-50" data-testid="pay-save">
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
        </button>
      </div>
    </div>
  );
}
